import { usePaginatedFields } from '@cibo/landmanager'
import CheckCircle from '@mui/icons-material/CheckCircleOutline'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { ascend, prop, propEq, sum } from 'ramda'
import { Fragment, ReactElement, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ControlGroup, useControlGroupsForAgreement } from '..'

const statusIcon: Record<ControlGroup['status'], ReactElement> = {
  pending: <CircularProgress />,
  invalid: <RadioButtonUnchecked />,
  valid: <CheckCircle />,
}

export type ControlGroupPickerProps = {
  value?: string
  onChange?: (id: string) => void
  agreementId?: string
}

type ControlGroupPickerListItemProps = {
  controlGroup: ControlGroup
  groupNumber: number
  onClick(item: { id: string }): void
  selected?: boolean
}

export const ControlGroupListItems = ({
  controlGroup,
  groupNumber,
  onClick,
  selected,
}: ControlGroupPickerListItemProps) => {
  const { t } = useTranslation('impact/components/ControlGroupPicker')

  const groupFields = usePaginatedFields(0, 1000, {
    termsets: [controlGroup.id],
  })

  const totalAcres = useMemo(() => sum(groupFields.data?.items.map(field => field.acres) || []), [
    groupFields.data,
  ])

  const plotDetail = controlGroup.details.find(propEq('traitId', 'geometry'))
  const plotAcres = plotDetail?.result.acres

  const StatusIcon = controlGroup.modelUnpaired ? (
    <RemoveCircleOutlineIcon />
  ) : (
    statusIcon[controlGroup.status]
  )

  return (
    <>
      <ListItem
        key={controlGroup.id}
        data-testid={`control-group-list-item_${controlGroup.id}`}
        selected={selected}
        onClick={() => onClick(controlGroup)}
        sx={{ width: '100%', cursor: 'pointer' }}
      >
        <ListItemIcon>{StatusIcon}</ListItemIcon>

        <ListItemText
          primary={t('groupName', { groupNumber })}
          primaryTypographyProps={{
            sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
          secondary={
            controlGroup.modelUnpaired
              ? t('modelUnpaired')
              : t('creditableAcres', { count: Math.max(totalAcres - plotAcres, 0) })
          }
        />

        {selected ? <ExpandLess /> : <ExpandMore />}
      </ListItem>

      <Divider />

      <Collapse in={selected}>
        {groupFields.data?.items.map(field => (
          <Fragment key={field.id}>
            <ListItem>
              <ListItemIcon></ListItemIcon>
              <ListItemText primary={field.name} />
            </ListItem>
            <Divider />
          </Fragment>
        ))}
      </Collapse>
    </>
  )
}

export const ControlGroupPicker = ({ value, onChange, agreementId }: ControlGroupPickerProps) => {
  const controlGroupsForAgreement = useControlGroupsForAgreement(agreementId)

  useEffect(() => {
    const firstGroup = controlGroupsForAgreement.data?.[0]
    if (firstGroup) {
      onChange && onChange(firstGroup.id)
    }
  }, [controlGroupsForAgreement.isFetched])

  const handleListItemClick = (item: { id: string }) => {
    onChange && onChange(item.id)
  }

  return (
    <List aria-label="available control groups" data-testid="control-group-picker">
      {controlGroupsForAgreement.data?.sort(ascend(prop('id'))).map((item, index) => (
        <ControlGroupListItems
          key={item.id}
          controlGroup={item}
          groupNumber={index + 1}
          onClick={handleListItemClick}
          selected={item.id === value}
        />
      ))}
    </List>
  )
}
